import type { ReactNode } from 'react';
import type { GuestCommunicationChannel, GuestCommunicationMessageKey } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { LANGUAGE_LABEL, type Lang } from './editorModel';

export type Mark = 'unsaved' | 'custom' | null;

const AUDIENCE: Record<GuestCommunicationChannel, { label: string; audience: string }> = {
  Email: { label: 'E-Mail', audience: 'Manuelle Buchungen' },
  Ota: { label: 'OTA-Nachricht', audience: 'Airbnb · Booking.com' },
};

/** Kanal samt Zielgruppe — wer diese Fassung überhaupt bekommt. */
export function ChannelSwitch({
  channels,
  active,
  marks,
  onChange,
}: {
  channels: GuestCommunicationChannel[];
  active: GuestCommunicationChannel;
  marks: Partial<Record<GuestCommunicationChannel, Mark>>;
  onChange: (channel: GuestCommunicationChannel) => void;
}) {
  return (
    <div role="group" aria-label="Kanal" className="flex flex-wrap gap-2">
      {channels.map((channel) => (
        <Choice key={channel} pressed={active === channel} onClick={() => onChange(channel)}>
          <span className="flex items-center gap-1.5 text-sm font-semibold">
            {AUDIENCE[channel].label}
            <Dot mark={marks[channel] ?? null} pressed={active === channel} />
          </span>
          <span className="text-[11.5px] font-normal opacity-80">{AUDIENCE[channel].audience}</span>
        </Choice>
      ))}
    </div>
  );
}

export function LanguageSwitch({
  active,
  marks,
  onChange,
}: {
  active: Lang;
  marks: Partial<Record<Lang, Mark>>;
  onChange: (lang: Lang) => void;
}) {
  return (
    <div role="group" aria-label="Sprache" className="bg-card-alt/60 inline-flex rounded-lg p-1">
      {(['De', 'En'] as const).map((lang) => (
        <button
          key={lang}
          type="button"
          aria-pressed={active === lang}
          onClick={() => onChange(lang)}
          className={cn(
            'focus-visible:outline-sage inline-flex min-h-11 items-center gap-1.5 rounded-md px-4 text-sm font-semibold focus-visible:outline focus-visible:outline-2',
            active === lang ? 'bg-navy text-paper' : 'text-ink-soft hover:text-ink',
          )}
        >
          {LANGUAGE_LABEL[lang]}
          <Dot mark={marks[lang] ?? null} pressed={active === lang} />
        </button>
      ))}
    </div>
  );
}

/** Was StayOn selbst setzt und deshalb kein Feld hat — so wie der Renderer es tut. */
export function SystemRow({
  messageKey,
  channel,
}: {
  messageKey: GuestCommunicationMessageKey;
  channel: GuestCommunicationChannel;
}) {
  const parts =
    channel === 'Ota'
      ? [
          'Portal-Link je gebuchtem Apartment',
          'zweite Sprache nach „---“',
          'kein Betreff, keine Anrede',
        ]
      : messageKey === 'PostStay'
        ? ['Anrede „Hallo Vorname,“', 'nur in der Sprache des Gasts']
        : [
            'Anrede „Hallo Vorname,“',
            'persönlicher Portal-Link',
            'zweite Sprache unter „English below“',
          ];
  return (
    <p className="border-line/60 bg-card-alt/40 text-ink-soft flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border px-3 py-2 text-xs">
      <span className="text-ink font-semibold">Setzt StayOn automatisch:</span>
      {parts.map((part) => (
        <span key={part}>{part}</span>
      ))}
    </p>
  );
}

function Choice(props: { pressed: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={props.pressed}
      onClick={props.onClick}
      className={cn(
        'focus-visible:outline-sage flex min-h-11 flex-col items-start rounded-lg border px-3.5 py-1.5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
        props.pressed ? 'border-navy bg-navy text-paper' : 'border-line text-ink bg-white',
      )}
    >
      {props.children}
    </button>
  );
}

function Dot({ mark, pressed }: { mark: Mark; pressed: boolean }) {
  if (!mark) return null;
  return (
    <>
      <span
        aria-hidden
        className={cn(
          'size-2 rounded-full',
          mark === 'unsaved' ? 'bg-amber' : pressed ? 'bg-paper/70' : 'bg-postmark',
        )}
      />
      <span className="sr-only">{mark === 'unsaved' ? '(ungespeichert)' : '(angepasst)'}</span>
    </>
  );
}
